/**
 * BTCNJCC Hardware-Accelerated 3D Viewport Page Turn Transition Engine
 * Pure GPU-composited 3D spatial leaf turn with perspective depth, dual-sided rendering,
 * traveling specular light beam, dynamic spine shadow, and viewport-fixed coordinates.
 */


export class PagePeelTransitionEngine {
  constructor(options = {}) {
    this.duration = options.duration || 520;
    this.isTransitioning = false;
    this.harnessEl = null;
    this.leafEl = null;
    this.frontEl = null;
    this.backEl = null;
    this.shadowEl = null;
    this.specularEl = null;
    this.currentAnimFrame = null;
    this.activeAnimations = null;
    this.initHarness();
  }

  /**
   * Pre-allocates the reusable fixed-viewport 3D transition harness in the DOM.
   */
  initHarness() {
    if (typeof document === 'undefined') return;
    let harness = document.getElementById('page-peel-harness');
    if (!harness) {
      harness = document.createElement('div');
      harness.id = 'page-peel-harness';
      harness.className = 'page-peel-harness is-idle';
      harness.setAttribute('aria-hidden', 'true');
      harness.innerHTML = `
        <div class="peel-3d-scene" id="peel-3d-scene">
          <div class="peel-incoming-shadow" id="peel-incoming-shadow"></div>
          <div class="peel-page-leaf" id="peel-page-leaf">
            <div class="peel-page-front" id="peel-page-front"></div>
            <div class="peel-page-back" id="peel-page-back">
              <div class="peel-carbon-texture"></div>
              <div class="peel-specular-beam" id="peel-specular-beam"></div>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(harness);
    }
    this.harnessEl = harness;
    this.leafEl = harness.querySelector('#peel-page-leaf');
    this.frontEl = harness.querySelector('#peel-page-front');
    this.backEl = harness.querySelector('#peel-page-back');
    this.shadowEl = harness.querySelector('#peel-incoming-shadow');
    this.specularEl = harness.querySelector('#peel-specular-beam');
  }

  /**
   * Check if reduced motion or headless environment is active.
   */
  shouldBypassAnimation() {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    if (document.documentElement.classList.contains('headless-mode') || document.body.classList.contains('headless-mode')) {
      return false; // Allow testing in headless mode unless reduced-motion requested
    }
    return false;
  }

  /**
   * Cancels any running transition immediately and resets the harness.
   */
  abortRunningTransition() {
    if (this.currentAnimFrame) {
      cancelAnimationFrame(this.currentAnimFrame);
      this.currentAnimFrame = null;
    }
    if (this.activeAnimations) {
      this.activeAnimations.forEach(a => { try { a.cancel(); } catch (_) {} });
      this.activeAnimations = null;
    }
    if (this.harnessEl) {
      this.harnessEl.className = 'page-peel-harness is-idle';
      this.harnessEl.style.display = 'none';
      if (this.frontEl) this.frontEl.innerHTML = '';
      if (this.leafEl) {
        this.leafEl.style.transform = '';
        this.leafEl.style.opacity = '';
      }
      if (this.shadowEl) {
        this.shadowEl.style.opacity = '';
        this.shadowEl.style.transform = '';
      }
      if (this.specularEl) {
        this.specularEl.style.transform = '';
      }
    }
    this.isTransitioning = false;
  }

  /**
   * Executes the 3D page turn transition over the visible viewport.
   * @param {HTMLElement} container - The main view container (e.g. #rides-container)
   * @param {Function} renderIncomingFn - Async or sync function that renders the destination view
   * @param {Object} options - { direction: 'forward'|'backward'|'tab_switch', duration: number }
   * @returns {Promise<void>}
   */
  async transition(container, renderIncomingFn, options = {}) {
    const mountEl = container || (typeof document !== 'undefined' && document.getElementById('rides-container'));
    if (!mountEl || typeof renderIncomingFn !== 'function') {
      if (typeof renderIncomingFn === 'function') await renderIncomingFn();
      return;
    }

    // Bypass animation if container is empty or in initial loading state
    const hasExistingContent = mountEl.children.length > 0 && !mountEl.querySelector('.loading-frame') && !mountEl.querySelector('.loading-state');
    if (!hasExistingContent || this.shouldBypassAnimation() || options.skipAnimation) {
      await renderIncomingFn(mountEl);
      return;
    }

    if (this.isTransitioning) {
      this.abortRunningTransition();
    }

    this.isTransitioning = true;
    const direction = options.direction || 'forward';
    const duration = options.duration || this.duration;

    if (!this.harnessEl) this.initHarness();

    // Snapshot outgoing view into front of the 3D leaf
    this.frontEl.innerHTML = '';
    const clone = mountEl.cloneNode(true);
    // Strip IDs from clone to avoid selector collisions
    clone.removeAttribute('id');
    const childIds = clone.querySelectorAll('[id]');
    childIds.forEach(el => el.removeAttribute('id'));

    // Prune offscreen items to prevent GPU layer memory bloat
    const cards = clone.querySelectorAll('.media-ride-card');
    if (cards.length > 10) {
      for (let i = 10; i < cards.length; i++) {
        if (typeof cards[i].remove === 'function') {
          cards[i].remove();
        } else if (cards[i].parentNode && typeof cards[i].parentNode.removeChild === 'function') {
          cards[i].parentNode.removeChild(cards[i]);
        }
      }
    }

    // Scroll to top smoothly if scrolled down before measuring geometry
    const scrollY = window.scrollY || window.pageYOffset || 0;
    if (scrollY > 0 && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }

    const rect = mountEl.getBoundingClientRect();
    clone.style.marginTop = `${rect.top}px`;
    clone.style.marginLeft = `${rect.left}px`;
    clone.style.width = `${rect.width}px`;

    this.frontEl.appendChild(clone);

    // Show harness and set active class
    this.harnessEl.className = `page-peel-harness is-peeling peel-dir-${direction}`;
    this.harnessEl.style.display = 'block';

    // Prepare decoupled incoming mount handler (executes asynchronously in background)
    let incomingMounted = false;
    let incomingMountPromise = null;
    const triggerMount = () => {
      if (incomingMounted) return incomingMountPromise;
      incomingMounted = true;
      incomingMountPromise = (async () => {
        try {
          await new Promise(r => {
            if (typeof requestAnimationFrame === 'function') {
              requestAnimationFrame(() => setTimeout(r, 0));
            } else {
              setTimeout(r, 0);
            }
          });
          await renderIncomingFn(mountEl);
        } catch (err) {
          console.warn('Error rendering incoming view inside 3D page turn:', err);
        }
      })();
      return incomingMountPromise;
    };

    // Use Web Animations API (WAAPI) for 100% GPU Compositor Thread execution when supported
    if (typeof this.leafEl.animate === 'function') {
      const keyframeCount = 32;
      const leafKeyframes = [];
      const shadowKeyframes = [];
      const specularKeyframes = [];

      for (let i = 0; i <= keyframeCount; i++) {
        const rawProgress = i / keyframeCount;
        const progress = rawProgress < 0.5
          ? 4 * rawProgress * rawProgress * rawProgress
          : 1 - Math.pow(-2 * rawProgress + 2, 3) / 2;
        const geom = this.computeFrameGeometry(progress, direction);
        leafKeyframes.push({
          transform: geom.leafTransform,
          opacity: geom.leafOpacity,
          transformOrigin: geom.transformOrigin
        });
        shadowKeyframes.push({
          transform: geom.shadowTransform,
          opacity: geom.shadowOpacity,
          transformOrigin: geom.shadowTransformOrigin
        });
        specularKeyframes.push({
          transform: `translateX(${geom.beamPercent.toFixed(1)}%)`
        });
      }

      // Trigger background mount
      triggerMount();

      const animOptions = { duration, fill: 'forwards', easing: 'linear' };
      const leafAnim = this.leafEl.animate(leafKeyframes, animOptions);
      const shadowAnim = this.shadowEl && typeof this.shadowEl.animate === 'function'
        ? this.shadowEl.animate(shadowKeyframes, animOptions)
        : null;
      const specularAnim = this.specularEl && typeof this.specularEl.animate === 'function'
        ? this.specularEl.animate(specularKeyframes, animOptions)
        : null;

      this.activeAnimations = [leafAnim, shadowAnim, specularAnim].filter(Boolean);

      return new Promise((resolve) => {
        let isDone = false;
        const done = () => {
          if (isDone) return;
          isDone = true;
          if (timer) clearTimeout(timer);
          this.finishTransition();
          resolve();
        };

        // Deterministic wall-clock fallback for headless browsers or timeline jitter
        const timer = setTimeout(done, duration + 25);

        const waitFinished = (anim) => {
          if (!anim) return Promise.resolve();
          if (anim.finished) return anim.finished;
          return new Promise(r => { anim.onfinish = r; });
        };

        Promise.all([
          waitFinished(leafAnim),
          incomingMountPromise || Promise.resolve()
        ]).then(done).catch(done);
      });
    }

    // Hardware-accelerated RAF animation fallback (e.g. Node tests / legacy engines)
    return new Promise((resolve) => {
      const startTime = performance.now();

      const animate = (now) => {
        const elapsed = now - startTime;
        const rawProgress = Math.min(1, Math.max(0, elapsed / duration));

        // Decoupled mounting: Trigger destination render mid-turn once leaf is lifted
        if (rawProgress >= 0.38 || duration <= 60) {
          triggerMount();
        }

        // Quintic S-Curve Easing (soft anticipation peel -> dynamic sweep -> cushioned settle)
        const progress = rawProgress < 0.5
          ? 4 * rawProgress * rawProgress * rawProgress
          : 1 - Math.pow(-2 * rawProgress + 2, 3) / 2;

        this.applyPeelFrame(progress, direction);

        if (rawProgress < 1 && this.isTransitioning) {
          this.currentAnimFrame = requestAnimationFrame(animate);
        } else {
          triggerMount();
          if (incomingMountPromise) {
            incomingMountPromise.finally(() => {
              this.finishTransition();
              resolve();
            });
          } else {
            this.finishTransition();
            resolve();
          }
        }
      };

      this.currentAnimFrame = requestAnimationFrame(animate);
    });
  }

  /**
   * Computes mathematical parameters for Compound Curvature 3D flex, shadow, and specular beam.
   */
  computeFrameGeometry(progress, direction) {
    let rotateAngle = 0;
    let rotateZ = 0;
    let skewY = 0;
    let liftZ = 0;
    let scaleX = 1;
    let beamPercent = 0;
    let shadowOpacity = 0;
    let transformOrigin = 'left center';
    let shadowTransformOrigin = 'left center';

    if (direction === 'backward') {
      // Flipping back: Page turns from left to right (0deg -> +160deg) around right spine
      rotateAngle = progress * 160;
      rotateZ = Math.sin(progress * Math.PI) * 3.8;
      skewY = Math.sin(progress * Math.PI) * 2.4;
      liftZ = Math.sin(progress * Math.PI) * 85;
      scaleX = 1 - Math.sin(progress * Math.PI) * 0.06;
      beamPercent = progress * 130;
      shadowOpacity = Math.sin(progress * Math.PI) * 0.55;
      transformOrigin = 'right center';
      shadowTransformOrigin = 'right center';
    } else if (direction === 'tab_switch') {
      // Tab switch: Smooth perspective tilt + cross-depth peel
      rotateAngle = progress * -140;
      rotateZ = Math.sin(progress * Math.PI) * -4.2;
      skewY = Math.sin(progress * Math.PI) * -2.0;
      liftZ = Math.sin(progress * Math.PI) * 75;
      scaleX = 1 - Math.sin(progress * Math.PI) * 0.05;
      beamPercent = progress * 120;
      shadowOpacity = Math.sin(progress * Math.PI) * 0.45;
      transformOrigin = 'right center';
      shadowTransformOrigin = 'right center';
    } else {
      // Default 'forward': Turning from right to left (0deg -> -160deg) with full follow-through
      rotateAngle = progress * -160;
      rotateZ = Math.sin(progress * Math.PI) * -3.8;
      skewY = Math.sin(progress * Math.PI) * -2.4;
      liftZ = Math.sin(progress * Math.PI) * 85;
      scaleX = 1 - Math.sin(progress * Math.PI) * 0.06;
      beamPercent = progress * 130;
      shadowOpacity = Math.sin(progress * Math.PI) * 0.55;
      transformOrigin = 'left center';
      shadowTransformOrigin = 'left center';
    }

    const leafOpacity = progress > 0.82 ? String(Math.max(0, (1 - progress) / 0.18)) : '1';
    const shadowScale = Math.min(1.2, Math.max(0.1, progress * 1.35));
    const shadowTransform = direction === 'backward'
      ? `scaleX(-${shadowScale.toFixed(3)})`
      : `scaleX(${shadowScale.toFixed(3)})`;

    return {
      leafTransform: `rotateY(${rotateAngle.toFixed(2)}deg) rotateZ(${rotateZ.toFixed(2)}deg) skewY(${skewY.toFixed(2)}deg) translateZ(${liftZ.toFixed(1)}px) scaleX(${scaleX.toFixed(3)})`,
      leafOpacity,
      transformOrigin,
      shadowTransform,
      shadowOpacity: String(shadowOpacity.toFixed(3)),
      shadowTransformOrigin,
      beamPercent
    };
  }

  /**
   * Computes and applies Compound Curvature 3D leaf flex, specular light beam position, and dynamic traveling shadow.
   */
  applyPeelFrame(progress, direction) {
    if (!this.leafEl) return;
    const geom = this.computeFrameGeometry(progress, direction);

    this.leafEl.style.transformOrigin = geom.transformOrigin;
    this.leafEl.style.transform = geom.leafTransform;
    this.leafEl.style.opacity = geom.leafOpacity;

    if (this.shadowEl) {
      this.shadowEl.style.transformOrigin = geom.shadowTransformOrigin;
      this.shadowEl.style.transform = geom.shadowTransform;
      this.shadowEl.style.opacity = geom.shadowOpacity;
    }

    if (this.specularEl) {
      this.specularEl.style.transform = `translateX(${geom.beamPercent.toFixed(1)}%)`;
    }
  }

  /**
   * Finalizes the transition and resets state.
   */
  finishTransition() {
    if (this.currentAnimFrame) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.currentAnimFrame);
      this.currentAnimFrame = null;
    }
    if (this.activeAnimations) {
      this.activeAnimations.forEach(a => { try { a.cancel(); } catch (_) {} });
      this.activeAnimations = null;
    }
    this.isTransitioning = false;
    if (this.harnessEl) {
      this.harnessEl.className = 'page-peel-harness is-idle';
      this.harnessEl.style.display = 'none';
    }
    if (this.frontEl) {
      this.frontEl.innerHTML = '';
    }
    if (this.leafEl) {
      this.leafEl.style.transform = '';
      this.leafEl.style.opacity = '';
    }
    if (this.shadowEl) {
      this.shadowEl.style.opacity = '';
      this.shadowEl.style.transform = '';
    }
    if (this.specularEl) {
      this.specularEl.style.transform = '';
    }
  }
}

// Global Singleton Instance
export const pagePeelEngine = new PagePeelTransitionEngine();

/**
 * Checks whether the current runtime environment is a desktop widescreen viewport (> 768px).
 */
export function isDesktopViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth > 768;
}

/**
 * Responsive transition dispatcher:
 * - Desktop & Mobile viewports: Instantaneous zero-transition view mount for clean, friction-free performance and optimal mobile framerates.
 * - Tactile 3D spatial page peel is preserved if explicitly requested via options.forceMobilePeel / options.forcePeelAnimation.
 */
export async function transitionWithPeel(container, renderIncomingFn, options = {}) {
  if (options?.forceMobilePeel || options?.forcePeelAnimation) {
    return pagePeelEngine.transition(container, renderIncomingFn, options);
  }
  if (typeof renderIncomingFn === 'function') {
    await renderIncomingFn(container);
  }
}

